/**
 * Turns the error core records on a workflow run into something the person
 * reading the request can act on.
 *
 * Core's errors are Go error chains meant for a log — `resolve user: context
 * key missing: ticket.field.4f3c…` names a slot, a failure mode and a UUID, and
 * says nothing to the admin looking at the request. The failure it describes is
 * almost always ordinary and fixable (a question was left blank), so it is
 * worth translating rather than hiding.
 *
 * Anything unrecognised is returned as-is: a message we can't place is still
 * better than silence, and it is the string to quote in a bug report.
 */

/** Where a rule reads a value from, as named by core's error prefix. */
type Slot = "user" | "resource" | "schedule";

export type WorkflowRunErrorContext = {
  /** Form question names by field id, for naming a `ticket.field.<uuid>` key. */
  fieldNameById: Map<string, string>;
};

const CONTEXT_KEY_PATTERN =
  /ticket\.(?:field\.[0-9a-f-]+|submitter|owner|resource_location)/i;

const FIELD_KEY_PREFIX = "ticket.field.";

/** The slot core was resolving when it failed, read off its error prefix. */
const slotOf = (message: string): Slot => {
  if (message.startsWith("resolve resource:")) return "resource";
  if (message.startsWith("resolve user:")) return "user";
  // A run_at failure surfaces from the dispatcher without a `resolve …` prefix.
  return "schedule";
};

/**
 * How to name the value behind a context key. A form-backed key is named by its
 * question; the rest are properties of the request itself.
 */
const describeKey = (
  key: string,
  context: WorkflowRunErrorContext
): string | null => {
  if (key === "ticket.resource_location") return "the workspace on the request";
  if (key === "ticket.submitter") return "the person who submitted the request";
  if (key === "ticket.owner") return "the request owner";
  if (!key.startsWith(FIELD_KEY_PREFIX)) return null;

  const name = context.fieldNameById.get(key.slice(FIELD_KEY_PREFIX.length));
  return name
    ? `the “${name}” answer`
    : "an answer that is no longer on the form";
};

const missingValueSentence = (slot: Slot, subject: string | null): string => {
  const named = subject ?? "a value the rule reads";
  if (slot === "schedule") {
    return `${capitalised(named)} was empty, so there was no date to wait for.`;
  }
  const what = slot === "resource" ? "no desk" : "nobody";
  return `${capitalised(named)} was empty, so there was ${what} to act on.`;
};

const capitalised = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * A sentence explaining why a run failed or was skipped, or the raw message
 * when it isn't one we know how to phrase.
 */
export const describeWorkflowRunError = (
  error: string,
  context: WorkflowRunErrorContext
): string => {
  const message = error.trim();
  if (!message) return message;

  const key = message.match(CONTEXT_KEY_PATTERN)?.[0] ?? null;
  const subject = key ? describeKey(key, context) : null;

  if (message.includes("context key missing")) {
    return missingValueSentence(slotOf(message), subject);
  }

  if (message.includes("context kind mismatch")) {
    return `${capitalised(subject ?? "a value the rule reads")} is no longer the kind of answer this rule needs — reopen the rule and point it at the right question.`;
  }

  if (message.includes("is not a date")) {
    return `${capitalised(subject ?? "the date this rule waits for")} isn't a date, so the run couldn't be scheduled.`;
  }

  if (message.includes("is not dedicated")) {
    return "That desk isn't set up as a dedicated desk, so it can't be held by one person.";
  }

  // `skip workflow run: resource <id> not found` / `… user <id> not found`
  if (message.includes("not found")) {
    if (message.includes("resource")) return "That desk no longer exists.";
    if (message.includes("user"))
      return "That person no longer has an account.";
  }

  if (message.includes("workflow no longer exists")) {
    return "The rule was deleted before it could run.";
  }

  if (message.includes("no executor registered")) {
    return "This rule does something this version of Tactic can't run.";
  }

  return message;
};
